import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MatExpansionPanel } from '@angular/material/expansion';
import { Language, UserSettings } from '@auth/login/data/login.model';
import {
  GoogleAnalyticsAction,
  GoogleAnalyticsCategory,
  GoogleAnalyticsLabel,
} from '@shared/google-analytics/google-analytics.model';
import { GoogleAnalyticsService } from '@shared/google-analytics/google-analytics.service';
import { FeatureFlagService } from '@shared/services/feature-flag/feature-flag.service';
import { CustomBranding, SessionFacade } from '@shared/store/session.facade';
import { IconColor, IconWeight } from '@widgets/eop-icon/eop-icon.component';
import { environment } from 'environments/environment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { isBrowserInGerman } from '../menu-footer/menu-footer.component';
import { getMenuPoints, MenuPoints } from '../menu-points.model';

@Component({
  selector: 'eop-header-menu',
  templateUrl: 'header-menu.component.html',
  styleUrls: ['header-menu.component.scss'],
  standalone: false,
})
export class HeaderMenuComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly featureFlagService = inject(FeatureFlagService);
  readonly sessionFacade = inject(SessionFacade);

  readonly CustomBranding = CustomBranding;
  private readonly sparkasseDataProtectionUrl =
    'https://s-emobility.de/api/document-service/v1/documents/9dff98cb-7677-429a-8fb8-3c893be7dd14/versions/current/download?language=en';
  private readonly enercityDataProtectionUrl = 'https://www.enercity.de/datenschutz';
  private readonly kommaDataProtectionUrl = 'https://1komma5.com/de/legal/datenschutzerklaerung';
  private readonly sparkasseImprint = 'https://www.s-einkauf.de/impressum/';
  private readonly enercityImprint = 'https://www.enercity.de/impressum';
  private readonly kommaImprint = 'https://1komma5.com/de/legal/impressum';

  screenWidth: number;
  $screenWidthDesktopMax = 1700;

  @HostListener('window:resize', ['$event'])
  onWindowResize() {
    this.handleTooltipsWithMenuCollapse();
  }

  @ViewChildren(MatExpansionPanel)
  expPanels: QueryList<MatExpansionPanel>;
  notification$ = this.sessionFacade.notification$;
  menuPoints: MenuPoints = { sections: [] };
  legalMenuPoints: MenuPoints = { sections: [] };
  isMenuCollapsed = false;
  activeMenuGroup: string;

  @Input()
  get menuCollapsed(): boolean {
    return this.isMenuCollapsed;
  }

  @Output()
  menuCollapsedChange = new EventEmitter<boolean>();

  set menuCollapsed(visible: boolean) {
    this.isMenuCollapsed = visible;
    this.menuCollapsedChange.emit(this.isMenuCollapsed);
  }

  isGermanLanguage = false;
  dataProtectionUrl: string;
  legalNoticeUrl: string;

  private readonly unsubscribe$ = new Subject<void>();

  ngOnInit() {
    this.setupLegalMenuPoints();
    this.setMenuPoints();
  }

  ngAfterViewInit() {
    this.handleTooltipsWithMenuCollapse();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  collapseMenu() {
    this.googleAnalyticsService.sendEvent(
      GoogleAnalyticsCategory.SIDE_MENU,
      GoogleAnalyticsAction.COLLAPSE,
      !this.isMenuCollapsed ? GoogleAnalyticsLabel.OPEN : GoogleAnalyticsLabel.CLOSE
    );
    this.menuCollapsed = !this.isMenuCollapsed;
    if (this.menuCollapsed) {
      this.sessionFacade.collapseSidebar();
    } else {
      this.sessionFacade.expandSidebar();
    }
    this.cdr.detectChanges();
  }

  private handleTooltipsWithMenuCollapse() {
    this.screenWidth = window.innerWidth;
    if (this.screenWidth > this.$screenWidthDesktopMax) {
      this.isMenuCollapsed = false;
      this.sessionFacade.expandSidebar();
    } else {
      this.isMenuCollapsed = true;
      this.sessionFacade.collapseSidebar();
    }
    this.cdr.detectChanges();
  }

  protected readonly IconWeight = IconWeight;
  protected readonly IconColor = IconColor;

  onMenuGroupChange(activeGroup: string) {
    this.activeMenuGroup = activeGroup;
  }

  protected readonly event = event;

  private setMenuPoints() {
    this.menuPoints = getMenuPoints();

    // GrowthBook is initialized centrally (SessionEffects). Rebuild the menu
    // whenever features are (re)evaluated — initial load, SSE push or
    // attribute change.
    this.featureFlagService.changes$.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.menuPoints = getMenuPoints();
      this.cdr.detectChanges();
    });
  }

  private setupLegalMenuPoints() {
    this.getUserLanguage();

    this.sessionFacade.branding$.subscribe(branding => {
      this.legalMenuPoints = {
        sections: [
          {
            name: 'HEADER.LEGAL',
            route: '/legal',
            testId: 'header-menu-legal',
            growthbookFlagId: 'header-menu-legal',
            icon: 'judge-outlined',
            links: [
              {
                route:
                  branding === CustomBranding.sparkasse
                    ? this.sparkasseDataProtectionUrl
                    : branding === CustomBranding.enercity
                      ? this.enercityDataProtectionUrl
                      : branding === CustomBranding.komma
                        ? this.kommaDataProtectionUrl
                        : this.dataProtectionUrl,
                openInBlank: true,
                name: 'PRIVACY_POLICY.TITLE',
                testId: 'header-menu-legal-data-protection',
                growthbookFlagId: 'header-menu-legal-data-protection',
                icon: 'privacy-policies-outlined',
              },
              {
                route:
                  branding === CustomBranding.sparkasse
                    ? this.sparkasseImprint
                    : branding === CustomBranding.enercity
                      ? this.enercityImprint
                      : branding === CustomBranding.komma
                        ? this.kommaImprint
                        : this.legalNoticeUrl,
                openInBlank: true,
                name: 'IMPRESSUM.TITLE_NEW',
                testId: 'header-menu-legal-impressum',
                growthbookFlagId: 'header-menu-legal-impressum',
                icon: 'legal-notice-outlined',
              },
            ],
          },
        ],
      };
    });
  }

  private getUserLanguage() {
    this.sessionFacade.userSettings$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((settings: UserSettings) => {
        this.isGermanLanguage = settings
          ? settings.language === Language.German
          : isBrowserInGerman();
        this.dataProtectionUrl = this.isGermanLanguage
          ? environment.dataProtectionUrlDE
          : environment.dataProtectionUrlEN;
        this.legalNoticeUrl = this.isGermanLanguage
          ? environment.legalNoticeUrlDE
          : environment.legalNoticeUrlEN;
      });
  }
}
